import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import {
  CustomTableCell,
  CustomTableRow,
  CustomTextStyle,
  CustomParagraph,
  CustomListItem,
  ResizableImage,
  CustomTable,
  DynamicMention,
  CustomTableHeader,
} from "@/components/documents/extensions";
import { setReportBuilderContextForMentions } from "@/components/documents/extensions/DynamicMention";
import { Extension } from "@tiptap/core";
import {
  A4_DIMENSIONS_PX,
  type Page,
  type ReportItem,
} from "@/components/common/report-builder/constants";
import {
  generateUniqueId,
  initialPage,
  generateDefaultBasicTable,
} from "@/components/common/report-builder/utils";
import { initializeDynamicValues } from "@/components/common/report-builder/dynamicValues";
import { arrayMove } from "@dnd-kit/sortable";
import {
  fetchScreenerColumns,
  type ScreenerColumn,
} from "@/services/api/screener";
import {
  createReport,
  deleteReport,
  getReport,
  listAllReports,
  updateReport,
} from "@/services/api/report-builder";
import { logPdfGenerationBuild } from "@/utils/pdfGenerationBuild";
import {
  fetchAvailableExposures,
  fetchAvailableStatistics,
  type ExposureType,
  type StatisticField,
} from "@/services/api/widget/chart-options";
import type {
  Report as ApiReport,
  ReportData,
  Page as ApiPage,
  ResourceSlot,
  ConfigOptions,
  CreateReportRequest,
  UpdateReportRequest,
} from "@/services/api/report-builder/types";
import type { CompanyTheme } from "@/services/api/themes/types";
import { useCompanyTheme } from "@/hooks/useCompanyTheme";
import { useEditorManager } from "@/hooks/useEditorManager";
import {
  captureCanvasThumbnail,
  waitForIdle,
} from "@/components/common/report-builder/utils/captureCanvasThumbnail";
import {
  useHistoryManager,
  type DocumentState,
} from "@/hooks/useHistoryManager";
import {
  useZoom,
  type ZoomState,
  type ZoomActions,
  ZOOM_MIN as ZOOM_MIN_CONST,
  ZOOM_MAX as ZOOM_MAX_CONST,
} from "@/hooks/useZoom";

/**
 * Factory function to create fresh TipTap extension instances.
 * IMPORTANT: Extensions must be created fresh for each editor instance
 * because they maintain internal state (this.editor) that becomes stale
 * when an editor is destroyed. Sharing extension instances across editors
 * causes "dispatchTransaction undefined" errors in React 18 StrictMode.
 */
const createEditorExtensions = () => [
  StarterKit.configure({
    link: false,
    // StarterKit v3 registers Underline by default — keep a single instance below.
    underline: false,
    // Configure paragraph behavior
    paragraph: false,
    // Use custom listItem that propagates text color to <li> for marker styling
    listItem: false,
    // Configure hard breaks for line breaks
    hardBreak: {
      keepMarks: true,
      HTMLAttributes: {
        class: "block",
      },
    },
  }),
  // Custom ListItem that copies text color to <li> element for ::marker styling
  CustomListItem,
  // Paragraph with style attribute support; also keep default margin reset
  CustomParagraph.configure({
    HTMLAttributes: {
      class: "mb-0",
    },
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["left", "center", "right", "justify"],
  }),
  Underline,
  CustomTextStyle,
  Highlight.configure({ multicolor: true }),
  ResizableImage.configure({ inline: false, allowBase64: true }),
  CustomTable.configure({ resizable: true, allowTableNodeSelection: true }),
  DynamicMention,
  CustomTableRow,
  CustomTableCell,
  CustomTableHeader,
  Link.configure({
    openOnClick: true,
    autolink: true,
    linkOnPaste: true,
  }),
  Placeholder.configure({
    placeholder: "Enter text here",
    showOnlyWhenEditable: false,
    showOnlyCurrent: false,
  }),
  Extension.create({
    name: "customEnter",
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          // Check if we're in a list - if so, let the list extension handle it
          if (
            this.editor.isActive("bulletList") ||
            this.editor.isActive("orderedList")
          ) {
            return false; // Let the default list behavior handle Enter
          }

          // Regular Enter creates a line break for non-list content
          return this.editor.commands.first(({ commands }) => [
            () =>
              commands.insertContent({
                type: "hardBreak",
              }),
          ]);
        },
        "Shift-Enter": () => {
          // Shift+Enter creates a new paragraph
          return this.editor.commands.first(({ commands }) => [
            () => commands.splitBlock(),
          ]);
        },
      };
    },
  }),
];

const HEADING_SIZE_CONFIG: Record<
  1 | 2 | 3,
  { fontSize: number; lineHeight: number }
> = {
  1: { fontSize: 32, lineHeight: 1.2 },
  2: { fontSize: 24, lineHeight: 1.3 },
  3: { fontSize: 18, lineHeight: 1.3 },
};
const HEADING_WIDTH_RATIO = 2 / 3;
const HEADING_VERTICAL_PADDING = 6;

// Extended ZoomActions for context (adds fitToWindow which requires page context)
interface ContextZoomActions extends ZoomActions {
  fitToWindow: () => void;
}

// Context Definition
interface ReportBuilderContextType {
  pages: Page[];
  setPages: React.Dispatch<React.SetStateAction<Page[]>>;
  pagesOrder: string[];
  setPagesOrder: React.Dispatch<React.SetStateAction<string[]>>;
  currentPageIndex: number;
  setCurrentPageIndex: React.Dispatch<React.SetStateAction<number>>;
  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  editingItemId: string | null;
  setEditingItemId: React.Dispatch<React.SetStateAction<string | null>>;
  editingArea: "header" | "footer" | null;
  setEditingArea: React.Dispatch<
    React.SetStateAction<"header" | "footer" | null>
  >;
  headerItems: ReportItem[];
  setHeaderItems: React.Dispatch<React.SetStateAction<ReportItem[]>>;
  footerItems: ReportItem[];
  setFooterItems: React.Dispatch<React.SetStateAction<ReportItem[]>>;
  clipboard: Omit<ReportItem, "id">[];
  setClipboard: React.Dispatch<React.SetStateAction<Omit<ReportItem, "id">[]>>;
  clipboardSourcePageId: string | null;
  setClipboardSourcePageId: React.Dispatch<React.SetStateAction<string | null>>;
  savedReports: ApiReport[];
  setSavedReports: React.Dispatch<React.SetStateAction<ApiReport[]>>;
  reportsLoading: boolean;
  currentReportId: string | null;
  setCurrentReportId: React.Dispatch<React.SetStateAction<string | null>>;
  currentReport: ApiReport | null;
  setCurrentReport: React.Dispatch<React.SetStateAction<ApiReport | null>>;
  orientation: "portrait" | "landscape";
  setOrientation: React.Dispatch<
    React.SetStateAction<"portrait" | "landscape">
  >;
  resourceSlots: ResourceSlot[];
  setResourceSlots: React.Dispatch<React.SetStateAction<ResourceSlot[]>>;
  configOptions: ConfigOptions;
  setConfigOptions: React.Dispatch<React.SetStateAction<ConfigOptions>>;
  companyTheme: CompanyTheme | null;
  isLoadingTheme: boolean;

  // Editor management
  editorManager: ReturnType<typeof useEditorManager>;
  activeEditor: Editor | null;
  // Removed setActiveEditor - no longer needed

  // Save status
  isSaving: boolean;
  lastSavedTime: Date | null;
  showSaveSuccess: boolean;
  saveError: string | null;
  showSaveError: boolean;
  hasUnsavedChanges: boolean;
  isLoadingReport: boolean;

  // Derived state and actions
  currentPage: Page | undefined;
  selectedReportItems: ReportItem[];
  selectedReportItem: ReportItem | null;
  addReportItem: (type: ReportItem["type"], data?: Partial<ReportItem>) => void;
  updateReportItem: (id: string, newProps: Partial<ReportItem>) => void;
  deleteItem: (id: string) => void;
  findReportItem: (id: string) => ReportItem | undefined;
  addPage: (isTitlePage?: boolean, templatePage?: Partial<Page>) => void;
  duplicatePage: (pageId: string) => void;
  deletePage: (pageId: string) => void;
  reorderPages: (oldIndex: number, newIndex: number) => void;
  toggleTitlePage: (pageId: string) => void;
  renamePage: (pageId: string, newName: string) => void;
  updatePage: (pageId: string, newProps: Partial<Page>) => void;
  handleOrientationChange: (orientation: "portrait" | "landscape") => void;
  handleSelect: (
    type: "item" | "header" | "footer",
    id?: string,
    event?: { shiftKey?: boolean; metaKey?: boolean }
  ) => void;
  handleCopy: () => void;
  handleCut: () => void;
  handlePaste: (customPosition?: { x: number; y: number }) => void;
  loadReport: (id: string) => Promise<void>;
  deleteSavedReport: (reportId: string) => Promise<void>;
  saveReport: (
    overrideData?: Partial<ReportData>,
    options?: { generatorReview?: boolean },
  ) => Promise<ApiReport | void>;
  saveReportAs: (
    name: string,
    options?: { description?: string | null },
  ) => Promise<ApiReport | void>;
  updateReportName: (name: string) => void;
  updateReportDescription: (description: string) => void;
  /** Replaces the report’s catalogue tag ids (`tags` on the API). */
  updateReportTags: (tagIds: string[]) => void;
  handleNewReport: () => void;
  createEditorExtensions: () => any[];
  availableColumns: ScreenerColumn[];
  availableExposures: ExposureType[];
  availableStatistics: StatisticField[];
  updatePagePadding: (pageId: string, padding: number) => void;
  navigateToPage: (pageIndex: number) => void | Promise<void>;

  // History management
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  startHistoryBatch: () => void;
  endHistoryBatch: () => void;
  pushToHistory: () => void;
  syncHistoryWithDocument: () => void;

  // Zoom management
  zoom: ZoomState;
  zoomActions: ContextZoomActions;

  // Canvas view preferences (rulers, grid, snap to grid)
  reportBuilderView: {
    gridSize: number | null;
    showGrid: boolean;
    showRulers: boolean;
    snapToGrid: boolean;
    gridColor: string;
  };
  setReportBuilderView: React.Dispatch<
    React.SetStateAction<{
      gridSize: number | null;
      showGrid: boolean;
      showRulers: boolean;
      snapToGrid: boolean;
      gridColor: string;
    }>
  >;

  // Auto-save preference
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (enabled: boolean) => void;

  /** Marks the report as having unsaved changes and triggers debounced auto-save */
  markUnsavedAndTriggerAutoSave: () => void;

  /**
   * Registers the function that opens the chart data assignment modal (wired from PropertiesPanel).
   * Pass null to unregister.
   */
  setOpenChartDataAssignmentHandler: (handler: (() => void) | null) => void;

  /**
   * Opens the chart data assignment dialog when the handler is registered (e.g. after adding a cash flow chart from the toolbar).
   */
  requestOpenChartDataAssignment: () => void;
}

const ReportBuilderContext = createContext<ReportBuilderContextType | null>(
  null
);

// Context Provider
export const ReportBuilderProvider: React.FC<{
  children: React.ReactNode;
  searchParams?: URLSearchParams;
  setSearchParams?: (
    params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    options?: { replace?: boolean }
  ) => void;
}> = ({ children, searchParams, setSearchParams }) => {
  const [pages, setPages] = useState<Page[]>([initialPage]);
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(
    "portrait"
  );
  const [resourceSlots, setResourceSlots] = useState<ResourceSlot[]>([]);
  const [configOptions, setConfigOptions] = useState<ConfigOptions>({});

  // Load company theme
  const { theme: companyTheme, isLoading: isLoadingTheme } = useCompanyTheme();

  // Zoom state management (using extracted hook)
  const {
    zoom,
    zoomActions: baseZoomActions,
    roundToZoomStep,
  } = useZoom();

  // Use constants from the hook for backward compatibility in this file
  const ZOOM_MIN = ZOOM_MIN_CONST;
  const ZOOM_MAX = ZOOM_MAX_CONST;

  // Default view preferences
  const defaultViewPrefs = {
    gridSize: 10 as number | null,
    showGrid: false,
    showRulers: true,
    snapToGrid: true,
    gridColor: "#6366f1",
  };

  // Initialize view preferences from localStorage
  const initializeViewPrefs = (): typeof defaultViewPrefs => {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return defaultViewPrefs;
    }

    try {
      const saved = localStorage.getItem("report-builder-view-prefs");
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        return { ...defaultViewPrefs, ...parsed };
      }
    } catch (error) {
      console.warn(
        "Failed to load view preferences from localStorage:",
        error
      );
    }
    return defaultViewPrefs;
  };

  const [reportBuilderView, setReportBuilderViewState] =
    useState(initializeViewPrefs);

  // Wrapper that persists view preferences to localStorage
  const setReportBuilderView: React.Dispatch<
    React.SetStateAction<typeof defaultViewPrefs>
  > = useCallback((update) => {
    setReportBuilderViewState((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      if (
        typeof window !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        try {
          localStorage.setItem("report-builder-view-prefs", JSON.stringify(next));
        } catch (error) {
          console.warn(
            "Failed to save view preferences to localStorage:",
            error
          );
        }
      }
      return next;
    });
  }, []);

  // Initialize auto-save preference from localStorage or default to true
  const initializeAutoSave = (): boolean => {
    // Check if we're in a browser environment (not SSR)
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return true; // Default to enabled during SSR
    }

    try {
      const saved = localStorage.getItem("report-builder-auto-save");
      if (saved !== null) {
        return JSON.parse(saved) === true;
      }
    } catch (error) {
      console.warn(
        "Failed to load auto-save preference from localStorage:",
        error
      );
    }
    return true; // Default to enabled
  };

  const [autoSaveEnabled, setAutoSaveEnabledState] =
    useState<boolean>(initializeAutoSave);

  // Persist auto-save preference to localStorage
  const setAutoSaveEnabled = useCallback((enabled: boolean) => {
    setAutoSaveEnabledState(enabled);
    // Only access localStorage in browser environment
    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(
          "report-builder-auto-save",
          JSON.stringify(enabled)
        );
      } catch (error) {
        console.warn(
          "Failed to save auto-save preference to localStorage:",
          error
        );
      }
    }
  }, []);


  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveError, setShowSaveError] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const saveTimerRef = React.useRef<number | null>(null);
  const saveFnRef = React.useRef<() => Promise<any>>(async () => {});
  const tagsDirtyRef = useRef(false);

  useEffect(() => {
    saveFnRef.current = async () => {
      try {
        await saveReport();
      } catch {
        // no-op
      }
    };
  });

  const [pagesOrder, setPagesOrder] = useState<string[]>(
    pages.map((p) => p.id)
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingArea, setEditingArea] = useState<"header" | "footer" | null>(
    null
  );
  const [headerItems, setHeaderItems] = useState<ReportItem[]>([]);
  const [footerItems, setFooterItems] = useState<ReportItem[]>([]);
  const [clipboard, setClipboard] = useState<Omit<ReportItem, "id">[]>([]);
  const [clipboardSourcePageId, setClipboardSourcePageId] = useState<
    string | null
  >(null);
  const [savedReports, setSavedReports] = useState<ApiReport[]>([]);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);
  const [currentReport, setCurrentReport] = useState<ApiReport | null>(null);
  const [reportsLoading, setReportsLoading] = useState<boolean>(true);
  // Removed activeEditor state - now computed from editorManager
  const [availableColumns, setAvailableColumns] = useState<ScreenerColumn[]>(
    []
  );
  const [availableExposures, setAvailableExposures] = useState<ExposureType[]>(
    []
  );
  const [availableStatistics, setAvailableStatistics] = useState<
    StatisticField[]
  >([]);

  /**
   * Marks the report as having unsaved changes and triggers auto-save after a debounce delay.
   * Use this in all mutation functions to ensure changes are tracked and auto-saved.
   */
  const markUnsavedAndTriggerAutoSave = useCallback(() => {
    setHasUnsavedChanges(true);

    if (autoSaveEnabled) {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        if (currentReportId && !isLoadingReport) {
          saveFnRef.current();
        }
      }, 1000);
    }
  }, [autoSaveEnabled, currentReportId, isLoadingReport]);

  useEffect(() => {
    initializeDynamicValues();
  }, []);

  // Initialize editor manager
  const editorManager = useEditorManager();

  // Efficient deep clone for report data (avoids expensive JSON serialization)
  const deepClone = useCallback(<T,>(obj: T): T => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as T;
    if (Array.isArray(obj)) return obj.map((item) => deepClone(item)) as T;

    const cloned = {} as T;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  }, []);

  // Initialize history manager with initial document state
  const initialDocState: DocumentState = {
    pages: [initialPage],
    pagesOrder: [initialPage.id],
    headerItems: [],
    footerItems: [],
    orientation: "portrait",
  };
  const historyManager = useHistoryManager<DocumentState>(initialDocState);
  
  // Track when we're in an undo/redo operation to prevent auto-sync
  const isUndoRedoRef = useRef(false);

  // Helper to get current document state for history
  const getDocumentState = useCallback((): DocumentState => {
    return {
      pages: deepClone(pages),
      pagesOrder: [...pagesOrder],
      headerItems: deepClone(headerItems),
      footerItems: deepClone(footerItems),
      orientation,
    };
  }, [pages, pagesOrder, headerItems, footerItems, orientation, deepClone]);

  // Helper to apply a document state from history
  const applyDocumentState = useCallback((state: DocumentState) => {
    setPages(state.pages);
    setPagesOrder(state.pagesOrder);
    setHeaderItems(state.headerItems);
    setFooterItems(state.footerItems);
    setOrientation(state.orientation);
    setEditingItemId(null);
  }, []);

  // Push current state to history before a mutation
  const pushToHistory = useCallback(() => {
    historyManager.pushState(getDocumentState());
    // Mark that we need to sync present after the mutation is applied
    needsSyncRef.current = true;
  }, [historyManager, getDocumentState]);

  // Sync history's present state with the current document (call after mutations)
  const syncHistoryWithDocument = useCallback(() => {
    historyManager.replace(getDocumentState());
  }, [historyManager, getDocumentState]);

  // Track if we need to sync history after next state update
  const needsSyncRef = useRef(false);
  // Batch operations end before the final React state is committed; sync present after commit
  const pendingPresentSyncAfterBatchRef = useRef(false);
  
  // Auto-sync history present state after document changes (but not during undo/redo)
  // We need historyManager.past.length in deps to ensure we run after pushState updates past
  const historyPastLength = historyManager.past.length;
  useEffect(() => {
    if (isUndoRedoRef.current) {
      return;
    }

    if (needsSyncRef.current && historyPastLength > 0) {
      needsSyncRef.current = false;
      historyManager.replace(getDocumentState());
      return;
    }

    if (pendingPresentSyncAfterBatchRef.current) {
      pendingPresentSyncAfterBatchRef.current = false;
      historyManager.replace(getDocumentState());
    }
  }, [pages, pagesOrder, headerItems, footerItems, orientation, historyManager, getDocumentState, historyPastLength]);
  
  // Mark that we need to sync after the next state update
  const markNeedsSync = useCallback(() => {
    needsSyncRef.current = true;
  }, []);

  // Undo action
  const undo = useCallback(() => {
    isUndoRedoRef.current = true;
    const prevState = historyManager.undo();
    if (prevState) {
      applyDocumentState(prevState);
    }
    // Reset after a microtask to ensure state has been applied
    Promise.resolve().then(() => { isUndoRedoRef.current = false; });
  }, [historyManager, applyDocumentState]);

  // Redo action
  const redo = useCallback(() => {
    isUndoRedoRef.current = true;
    const nextState = historyManager.redo();
    if (nextState) {
      applyDocumentState(nextState);
    }
    // Reset after a microtask to ensure state has been applied
    Promise.resolve().then(() => { isUndoRedoRef.current = false; });
  }, [historyManager, applyDocumentState]);

  // Start/end batch operations for continuous actions like drag/resize
  const startHistoryBatch = useCallback(() => {
    // Capture current state before starting the batch
    historyManager.pushState(getDocumentState());
    historyManager.startBatch();
  }, [historyManager, getDocumentState]);

  const endHistoryBatch = useCallback(() => {
    historyManager.endBatch();
    pendingPresentSyncAfterBatchRef.current = true;
  }, [historyManager]);

  // Derived history state
  const canUndo = historyManager.canUndo;
  const canRedo = historyManager.canRedo;

  // Sync activeEditor with editorManager - FIXED: Prevent unnecessary updates
  const activeEditor = useMemo(() => {
    return editorManager.getActiveEditor();
  }, [editorManager.activeEditorId]);

  useEffect(() => {
    const load = async () => {
      const [columns, exposures, statistics] = await Promise.all([
        fetchScreenerColumns(),
        fetchAvailableExposures(),
        fetchAvailableStatistics(),
      ]);
      setAvailableColumns(columns);
      setAvailableExposures(exposures);
      setAvailableStatistics(statistics);
    };
    load();
  }, []);

  useEffect(() => {
    // PDF generation only renders one report; paginating the full catalog (often
    // 10+ requests) adds noise to the ready monitor and delays capture.
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/pdf"
    ) {
      logPdfGenerationBuild("skip-listAllReports-on-pdf-route");
      return;
    }

    const fetchReports = async () => {
      try {
        setReportsLoading(true);
        const reports = await listAllReports();
        if (Array.isArray(reports)) {
          setSavedReports(reports);
        } else {
          console.error("listAllReports did not return an array:", reports);
          setSavedReports([]);
        }
      } catch (error) {
        console.error("Failed to fetch reports", error);
        setSavedReports([]);
      } finally {
        setReportsLoading(false);
      }
    };
    fetchReports();
  }, []);

  // Preload capture dependency in background for faster first capture
  useEffect(() => {
    // Give the app a moment to settle before loading
    const id = setTimeout(() => {
      import("html-to-image").catch(() => {});
    }, 500);
    return () => clearTimeout(id);
  }, []);

  const orderedPages = useMemo(
    () =>
      pagesOrder
        .map((pageId) => pages.find((p) => p.id === pageId))
        .filter((p): p is Page => !!p),
    [pages, pagesOrder]
  );

  const currentPage = useMemo(
    () => orderedPages[currentPageIndex],
    [orderedPages, currentPageIndex]
  );

  // Zoom actions (defined after orderedPages to avoid initialization error)
  // Wrap base zoom actions with fitToWindow that has page context
  const zoomActions = useMemo<ContextZoomActions>(
    () => ({
      ...baseZoomActions,
      fitToWindow: () => {
        if (currentPage) {
          baseZoomActions.fitToSize(
            currentPage.size.width,
            currentPage.size.height
          );
        } else {
          // Fallback to reset zoom if no current page
          baseZoomActions.resetZoom();
        }
      },
    }),
    [baseZoomActions, currentPage]
  );

  const selectedReportItems = useMemo(() => {
    return selectedItems
      .map((id) => {
        return (
          headerItems.find((item) => item.id === id) ||
          footerItems.find((item) => item.id === id) ||
          currentPage?.items.find((item) => item.id === id)
        );
      })
      .filter((item): item is ReportItem => !!item);
  }, [selectedItems, headerItems, footerItems, currentPage]);

  const selectedReportItem =
    selectedReportItems.length > 0 ? selectedReportItems[0] : null;

  const getAreaItems = useCallback((): ReportItem[] => {
    if (!selectedReportItem) return [];
    // Determine which area the item belongs to
    const inHeader = headerItems.some((i) => i.id === selectedReportItem.id);
    const inFooter = footerItems.some((i) => i.id === selectedReportItem.id);
    if (inHeader) return headerItems;
    if (inFooter) return footerItems;
    return currentPage?.items || [];
  }, [selectedReportItem, headerItems, footerItems, currentPage]);

  const findReportItem = useCallback(
    (id: string) => {
      return (
        headerItems.find((item) => item.id === id) ||
        footerItems.find((item) => item.id === id) ||
        pages.flatMap((p) => p.items).find((item) => item.id === id)
      );
    },
    [headerItems, footerItems, pages]
  );

  const PIE_CHART_TYPES = new Set([
    "pie",
    "donut",
    "geographic-pie",
    "geographic-donut",
    "geographic-combined",
    "combined-standardized-regions",
  ]);

  const openChartDataAssignmentHandlerRef = useRef<(() => void) | null>(null);
  const setOpenChartDataAssignmentHandler = useCallback(
    (handler: (() => void) | null) => {
      openChartDataAssignmentHandlerRef.current = handler;
    },
    []
  );
  const requestOpenChartDataAssignment = useCallback(() => {
    window.setTimeout(() => {
      openChartDataAssignmentHandlerRef.current?.();
    }, 0);
  }, []);

  const addReportItem = useCallback(
    (type: ReportItem["type"], data: Partial<ReportItem> = {}) => {
      // Save current state to history before mutation
      pushToHistory();

      // Get theme defaults for this element type
      const rawThemeDefaults = companyTheme?.elementDefaults?.[type] || {};

      // Filter out 'presets' from text defaults as it's not a direct style property
      const themeDefaults =
        type === "text" && rawThemeDefaults.presets
          ? Object.fromEntries(
              Object.entries(rawThemeDefaults).filter(
                ([key]) => key !== "presets"
              )
            )
          : rawThemeDefaults;

      // For line shapes, calculate height based on thickness with buffer for selection/dragging
      const lineThickness = data.thickness || themeDefaults.thickness || 2;
      const LINE_BUFFER = 20; // 10px on each side for easier selection/dragging
      const lineHeight = lineThickness + LINE_BUFFER;

      // Establish a provisional size so we can center accurately
      const provisionalSize =
        type === "shape"
          ? data.shape === "line"
            ? { width: 200, height: lineHeight }
            : { width: 100, height: 100 }
          : { width: 300, height: 150 };

      const newItem: ReportItem = {
        id: generateUniqueId(type),
        type,
        content:
          type === "text"
            ? {
                type: "doc",
                content: [
                  {
                    type: "paragraph",
                    attrs: { style: "line-height: 1;" },
                    content: [],
                  },
                ],
              }
            : type === "table" &&
                (data as any)?.tableConfig?.tableMode === "basic"
              ? generateDefaultBasicTable()
              : type === "table" || type === "image"
                ? ""
                : `New ${type}`,
        position: { x: 50, y: 50 },
        size: provisionalSize,
        backgroundColor: type === "shape" ? "#E2E8F0" : "transparent",
        padding: 0,
        zIndex: 0,
        ...(type === "shape" &&
          data.shape === "line" && {
            thickness: lineThickness,
            lineStyle: "solid" as const,
          }),
        ...(type === "text" && {
          textConfig: {
            columnCount: 1,
            columnGap: 16,
            verticalAlign: "start",
          },
        }),
        ...(type === "table" && {
          tableConfig: {
            columns: [],
            filters: [],
            cellPadding: 5,
            // Apply table-specific theme defaults
            ...themeDefaults,
            // Merge user-provided tableConfig on top
            ...(data.tableConfig || {}),
          },
        }),
        ...(type === "chart" && {
          chartConfig: {
            chartType: undefined,
            dataSourceId: undefined,
          },
        }),
        // For non-table types, apply theme defaults at top level
        ...(type !== "table" && themeDefaults),
        // Apply user data (but not tableConfig which is already merged above)
        ...(type === "table" ? { ...data, tableConfig: undefined } : data),
        // Restore the properly merged tableConfig for tables
        ...(type === "table" && {
          tableConfig: {
            columns: [],
            filters: [],
            cellPadding: 5,
            ...themeDefaults,
            ...(data.tableConfig || {}),
          },
        }),
      };

      if (type === "chart") {
        const chartConfig = newItem.chartConfig || {};
        const currentSettings = chartConfig.settings || {};
        const chartType = chartConfig.chartType || "";
        const hasPieStyle =
          typeof currentSettings.pieStyle === "string" ||
          currentSettings.pieStyle === "pie";
        const matchesPieType =
          PIE_CHART_TYPES.has(chartType) ||
          chartType.toLowerCase().includes("pie");

        if (
          !("showLabels" in currentSettings) &&
          (hasPieStyle || matchesPieType)
        ) {
          currentSettings.showLabels = false;
        }

        newItem.chartConfig = {
          ...chartConfig,
          settings: currentSettings,
        };
      }

      if (type === "chart") {
        const pageSize =
          currentPage?.size ||
          A4_DIMENSIONS_PX[orientation] ||
          A4_DIMENSIONS_PX.portrait;
        const padding = 60;
        const isPortrait = orientation === "portrait";
        const targetWidth = Math.max(
          Math.round(
            isPortrait ? (pageSize.width * 2) / 3 : pageSize.width / 2
          ),
          provisionalSize.width
        );
        const targetHeight = Math.max(
          Math.round(pageSize.height / 2),
          provisionalSize.height
        );
        const maxWidth = Math.max(
          pageSize.width - padding,
          provisionalSize.width
        );
        const maxHeight = Math.max(
          pageSize.height - padding,
          provisionalSize.height
        );
        newItem.size = {
          width: Math.min(targetWidth, maxWidth),
          height: Math.min(targetHeight, maxHeight),
        };
        if (!data.position) {
          const pagePadding = currentPage?.padding || 0;
          const pageWidth = pageSize.width;
          const pageHeight = pageSize.height;
          const contentWidth = Math.max(0, pageWidth - pagePadding * 2);
          const contentHeight = Math.max(0, pageHeight - pagePadding * 2);
          const baseX =
            pagePadding + Math.max(0, (contentWidth - newItem.size.width) / 2);
          const baseY =
            pagePadding +
            Math.max(0, (contentHeight - newItem.size.height) / 2);
          newItem.position = {
            x: Math.round(Math.max(0, baseX)),
            y: Math.round(Math.max(0, baseY)),
          };
        }
      }

      if (type === "text" && data.headingLevel) {
        const headingConfig = HEADING_SIZE_CONFIG[data.headingLevel];
        if (headingConfig) {
          const fallbackWidth =
            A4_DIMENSIONS_PX[orientation]?.width ??
            A4_DIMENSIONS_PX.portrait.width;
          const pageWidth = currentPage?.size?.width ?? fallbackWidth;
          const computedWidth = Math.max(
            Math.round(pageWidth * HEADING_WIDTH_RATIO),
            provisionalSize.width
          );
          const computedHeight = Math.ceil(
            headingConfig.fontSize * headingConfig.lineHeight +
              HEADING_VERTICAL_PADDING
          );
          newItem.size = { width: computedWidth, height: computedHeight };
        }
      }

      // Compute initial position relative to current viewport center within the canvas
      const shouldCenterViaViewport = type !== "chart";
      try {
        if (
          shouldCenterViaViewport &&
          typeof window !== "undefined" &&
          !data.position
        ) {
          const canvasEl = document.getElementById("report-canvas");
          if (canvasEl && currentPage) {
            const rect = canvasEl.getBoundingClientRect();

            // Calculate screen coordinates
            const screenX =
              window.innerWidth / 2 - rect.left - newItem.size.width / 2;
            const screenY =
              window.innerHeight / 2 - rect.top - newItem.size.height / 2;

            // Adjust for zoom level - divide by zoom to get canvas coordinates
            const targetX = Math.round(screenX / zoom.level);
            const targetY = Math.round(screenY / zoom.level);

            const clampedX = Math.round(
              Math.max(
                0,
                Math.min(targetX, currentPage.size.width - newItem.size.width)
              )
            );
            const clampedY = Math.round(
              Math.max(
                0,
                Math.min(targetY, currentPage.size.height - newItem.size.height)
              )
            );
            newItem.position = { x: clampedX, y: clampedY };
          }
        }
      } catch {
        // fall back to default 50,50
      }

      // Route to header/footer if in edit mode
      if (editingArea === "header") {
        setHeaderItems((prev) => {
          const maxZ = prev.reduce((m, it) => Math.max(m, it.zIndex || 0), 0);
          return [...prev, { ...newItem, zIndex: maxZ + 1 }];
        });
        setSelectedItems([newItem.id]);
        return;
      }
      if (editingArea === "footer") {
        setFooterItems((prev) => {
          const maxZ = prev.reduce((m, it) => Math.max(m, it.zIndex || 0), 0);
          return [...prev, { ...newItem, zIndex: maxZ + 1 }];
        });
        setSelectedItems([newItem.id]);
        return;
      }

      const pageId = currentPage?.id;
      if (!pageId) return;

      setPages((prevPages) =>
        prevPages.map((page) =>
          page.id === pageId
            ? {
                ...page,
                items: [
                  ...page.items,
                  {
                    ...newItem,
                    zIndex:
                      Math.max(0, ...page.items.map((i) => i.zIndex || 0)) + 1,
                  },
                ],
              }
            : page
        )
      );

      // Select the newly created item
      setSelectedItems([newItem.id]);

      // Mark as unsaved when adding items
      setHasUnsavedChanges(true);
    },
    [
      currentPageIndex,
      currentPage,
      editingArea,
      companyTheme,
      zoom.level,
      orientation,
      pushToHistory,
    ]
  );

  const updateReportItem = useCallback(
    (id: string, newProps: Partial<ReportItem>) => {
      // Note: We don't push to history here by default because:
      // 1. Continuous operations (drag/resize) use batching via startHistoryBatch/endHistoryBatch
      // 2. Discrete property changes should call pushToHistory explicitly before calling this
      // This prevents flooding history with micro-changes during drag operations

      let itemLocation = "unknown";
      let foundInHeader = false;
      let foundInFooter = false;

      setHeaderItems((prev) => {
        const found = prev.some((item) => item.id === id);
        if (found) {
          foundInHeader = true;
          itemLocation = "header";
        }
        return prev.map((item) =>
          item.id === id ? { ...item, ...newProps } : item
        );
      });
      setFooterItems((prev) => {
        const found = prev.some((item) => item.id === id);
        if (found && !foundInHeader) {
          foundInFooter = true;
          itemLocation = "footer";
        }
        return prev.map((item) =>
          item.id === id ? { ...item, ...newProps } : item
        );
      });
      let itemFoundInPages = false;
      setPages((prevPages) =>
        prevPages.map((page, idx) => {
          const items = page.items;
          let changed = false;
          const nextItems = items.map((item) => {
            if (item.id === id) {
              changed = true;
              itemFoundInPages = true;
              if (!foundInHeader && !foundInFooter) {
                itemLocation = "pages";
              }
              return { ...item, ...newProps };
            }
            return item;
          });
          return changed
            ? {
                ...page,
                items: nextItems,
                thumbnailDirty:
                  idx === currentPageIndex ? true : page.thumbnailDirty,
              }
            : page;
        })
      );
      // Mark as unsaved when updating items
      setHasUnsavedChanges(true);

      // Auto-save after a short delay to avoid excessive saves (only if enabled)
      if (autoSaveEnabled) {
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = window.setTimeout(() => {
          // Only auto-save if we have a report ID and it's not currently loading
          if (currentReportId && !isLoadingReport) {
            saveFnRef.current();
          }
        }, 1000); // Save after 1 second of inactivity
      }
    },
    [currentReportId, currentPageIndex, autoSaveEnabled, isLoadingReport]
  );

  const deleteItem = useCallback(
    (id: string) => {
      // Save current state to history before mutation
      pushToHistory();

      // Unregister editor if it's being managed
      editorManager.unregisterEditor(id);

      setHeaderItems((prev) => prev.filter((item) => item.id !== id));
      setFooterItems((prev) => prev.filter((item) => item.id !== id));
      setPages((prevPages) =>
        prevPages.map((page, idx) => {
          const filtered = page.items.filter((item) => item.id !== id);
          const changed = filtered.length !== page.items.length;
          return changed
            ? {
                ...page,
                items: filtered,
                thumbnailDirty:
                  idx === currentPageIndex ? true : page.thumbnailDirty,
              }
            : page;
        })
      );
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
      if (editingItemId === id) {
        setEditingItemId(null);
      }

      // Mark as unsaved when deleting items
      setHasUnsavedChanges(true);

      // Auto-save after a short delay to avoid excessive saves (only if enabled)
      if (autoSaveEnabled) {
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = window.setTimeout(() => {
          // Only auto-save if we have a report ID and it's not currently loading
          if (currentReportId && !isLoadingReport) {
            saveFnRef.current();
          }
        }, 1000); // Save after 1 second of inactivity
      }
    },
    [editingItemId, currentPageIndex, pushToHistory, autoSaveEnabled, currentReportId, isLoadingReport]
  );

  /**
   * Update a page's thumbnail without triggering the thumbnailDirty flag.
   * Triggers auto-save to persist the thumbnail to the API.
   */
  const updatePageThumbnail = useCallback(
    (pageId: string, thumbnail: string) => {
      setPages((prevPages) => {
        const newPages = prevPages.map((page) =>
          page.id === pageId
            ? { ...page, thumbnail, thumbnailDirty: false }
            : page
        );
        return newPages;
      });
      // Trigger auto-save to persist the thumbnail
      markUnsavedAndTriggerAutoSave();
    },
    [markUnsavedAndTriggerAutoSave]
  );

  const navigateToPage = useCallback(
    async (pageIndex: number) => {
      // Capture thumbnail of current page BEFORE navigating away
      // This ensures we capture the current canvas content, not the new page
      const pageToCapture = currentPage;
      const shouldCapture =
        pageToCapture && (pageToCapture as any).thumbnailDirty === true;

      if (shouldCapture) {
        try {
          // Capture immediately before navigation - canvas still shows current page
          const thumbnail = await captureCanvasThumbnail();
          if (thumbnail && pageToCapture.id) {
            updatePageThumbnail(pageToCapture.id, thumbnail);
          } else {
            console.warn(
              "[navigateToPage] No thumbnail captured for page:",
              pageToCapture.id,
              "thumbnail:",
              thumbnail ? "exists but no id" : "null"
            );
          }
        } catch (err) {
          console.warn("[navigateToPage] Failed to capture thumbnail:", err);
        }
      }

      // Now perform navigation after capture
      setCurrentPageIndex(pageIndex);
      setSelectedItems([]); // Clear selections when navigating between pages
      setEditingItemId(null);
      setEditingArea(null);
      editorManager.disableAllEditing();

      // Update URL parameter if setSearchParams is available
      if (setSearchParams) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          if (pageIndex === 0) {
            // Remove page param for first page (default)
            newParams.delete("page");
          } else {
            newParams.set("page", pageIndex.toString());
          }
          return newParams;
        });
      }
    },
    [setSearchParams, currentPage, updatePageThumbnail]
  );

  const addPage = useCallback(
    (isTitlePage = false, templatePage?: Partial<Page>) => {
      // Save current state to history before mutation
      pushToHistory();

      const lastPage = currentPage;

      const newPage: Page = {
        id: `page-${Date.now()}`,
        name: templatePage?.name ?? `Page ${pages.length + 1}`,
        items: templatePage?.items ?? [],
        size:
          templatePage?.size ?? lastPage?.size ?? A4_DIMENSIONS_PX[orientation],
        isTitlePage: templatePage?.isTitlePage ?? isTitlePage,
        padding: templatePage?.padding ?? lastPage?.padding ?? 0,
        backgroundColor:
          templatePage?.backgroundColor ?? lastPage?.backgroundColor,
        backgroundImage:
          templatePage !== undefined
            ? templatePage.backgroundImage
            : lastPage?.backgroundImage,
        // Mark new pages as needing thumbnail capture
        thumbnailDirty: true,
      };
      setPages((prev) => [...prev, newPage]);
      setPagesOrder((prevOrder) => [...prevOrder, newPage.id]);
      navigateToPage(pages.length);

      markUnsavedAndTriggerAutoSave();
    },
    [
      pages.length,
      currentPage,
      orientation,
      navigateToPage,
      pushToHistory,
      markUnsavedAndTriggerAutoSave,
    ]
  );

  const duplicatePage = useCallback(
    (pageId: string) => {
      // Save current state to history before mutation
      pushToHistory();

      const pageIndex = pages.findIndex((p) => p.id === pageId);
      if (pageIndex === -1) return;

      const pageToDuplicate = pages[pageIndex];
      const newPage: Page = {
        ...pageToDuplicate,
        id: `page-${Date.now()}`,
        name: `${pageToDuplicate.name} (Copy)`,
        items: pageToDuplicate.items.map((item) => ({
          ...item,
          id: generateUniqueId(item.type),
        })),
        thumbnailDirty: true,
      };

      setPages((prev) => [...prev, newPage]);

      const targetOrderIndex = pagesOrder.findIndex((id) => id === pageId);
      const insertionIndex =
        targetOrderIndex === -1 ? pagesOrder.length : targetOrderIndex + 1;

      setPagesOrder((prevOrder) => {
        const nextOrder = [...prevOrder];
        nextOrder.splice(insertionIndex, 0, newPage.id);
        return nextOrder;
      });

      navigateToPage(insertionIndex);

      markUnsavedAndTriggerAutoSave();
    },
    [pages, pagesOrder, navigateToPage, pushToHistory, markUnsavedAndTriggerAutoSave]
  );

  const deletePage = useCallback(
    (pageId: string) => {
      // Save current state to history before mutation
      pushToHistory();

      setPages((prev) => prev.filter((p) => p.id !== pageId));
      setPagesOrder((prevOrder) => prevOrder.filter((id) => id !== pageId));
      setCurrentPageIndex((prevIndex) =>
        Math.max(0, Math.min(prevIndex, pages.length - 2))
      );

      markUnsavedAndTriggerAutoSave();
    },
    [pages.length, pushToHistory, markUnsavedAndTriggerAutoSave]
  );

  const reorderPages = useCallback(
    (oldIndex: number, newIndex: number) => {
      // Save current state to history before mutation
      pushToHistory();

      setPagesOrder((prevOrder) => arrayMove(prevOrder, oldIndex, newIndex));

      markUnsavedAndTriggerAutoSave();
    },
    [pushToHistory, markUnsavedAndTriggerAutoSave]
  );

  const toggleTitlePage = useCallback(
    (pageId: string) => {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.id === pageId
            ? { ...page, isTitlePage: !page.isTitlePage }
            : page
        )
      );

      markUnsavedAndTriggerAutoSave();
    },
    [markUnsavedAndTriggerAutoSave]
  );

  const renamePage = useCallback(
    (pageId: string, newName: string) => {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.id === pageId ? { ...page, name: newName } : page
        )
      );

      markUnsavedAndTriggerAutoSave();
    },
    [markUnsavedAndTriggerAutoSave]
  );

  const updatePagePadding = useCallback(
    (pageId: string, padding: number) => {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.id === pageId ? { ...page, padding, thumbnailDirty: true } : page
        )
      );

      markUnsavedAndTriggerAutoSave();
    },
    [markUnsavedAndTriggerAutoSave]
  );

  const updatePage = useCallback(
    (pageId: string, newProps: Partial<Page>) => {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.id === pageId
            ? { ...page, ...newProps, thumbnailDirty: true }
            : page
        )
      );

      markUnsavedAndTriggerAutoSave();
    },
    [markUnsavedAndTriggerAutoSave]
  );

  const handleOrientationChange = useCallback(
    (newOrientation: "portrait" | "landscape") => {
      // Only mark as unsaved if the orientation is actually changing
      const orientationChanged = newOrientation !== orientation;
      if (orientationChanged) {
        // Save current state to history before mutation
        pushToHistory();

        setOrientation(newOrientation);
        // Update all pages to use the new orientation and size
        setPages((prevPages) =>
          prevPages.map((page) => ({
            ...page,
            size: A4_DIMENSIONS_PX[newOrientation],
            thumbnailDirty: true,
          }))
        );
        // Mark as unsaved when changing orientation
        setHasUnsavedChanges(true);
      }
    },
    [orientation, pushToHistory]
  );

  const handleSelect = useCallback(
    (
      type: "item" | "header" | "footer",
      id?: string,
      event?: { shiftKey?: boolean; metaKey?: boolean }
    ) => {
      if (type === "item" && id) {
        if (!event?.shiftKey && !event?.metaKey) {
          // Single selection - use functional update to check current state
          setSelectedItems((prev) => {
            if (!prev.includes(id)) {
              setEditingItemId(null);
            }
            return [id];
          });
        } else if (event.shiftKey || event.metaKey) {
          // Add/toggle selection with Shift or Cmd/Ctrl
          setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
          );
        }
        // Do not exit header/footer edit mode on item selection; edit mode exits only via canvas click-off or close button.
      } else if (type === "header") {
        setSelectedItems([]);
        setEditingArea("header");
        setEditingItemId(null);
      } else if (type === "footer") {
        setSelectedItems([]);
        setEditingArea("footer");
        setEditingItemId(null);
      } else {
        setSelectedItems([]);
        setEditingArea(null);
        setEditingItemId(null);
        editorManager.disableAllEditing();
      }
    },
    [] // Removed selectedItems dependency to prevent stale closures - using functional updates instead
  );

  const handleCopy = useCallback(() => {
    if (selectedReportItems.length > 0) {
      const itemsToCopy = selectedReportItems.map((item) => {
        const { id, ...rest } = item;
        return rest;
      });
      setClipboard(itemsToCopy);
      // Track the page where the copy happened to preserve absolute positions on paste across slides
      setClipboardSourcePageId(currentPage?.id ?? null);
    }
  }, [selectedReportItems, currentPage]);

  const handleCut = useCallback(() => {
    if (selectedReportItems.length > 0) {
      handleCopy();
      selectedReportItems.forEach((item) => deleteItem(item.id));
    }
  }, [selectedReportItems, handleCopy, deleteItem]);

  const handlePaste = useCallback(
    (customPosition?: { x: number; y: number }) => {
      if (clipboard.length === 0) return;

      // Save current state to history before mutation
      pushToHistory();

      const isDifferentSlide =
        clipboardSourcePageId && currentPage
          ? clipboardSourcePageId !== currentPage.id
          : false;

      // Calculate the bounding box of the copied items
      const boundingBox = clipboard.reduce(
        (box, item) => {
          const itemRight = item.position.x + item.size.width;
          const itemBottom = item.position.y + item.size.height;
          return {
            left: Math.min(box.left, item.position.x),
            top: Math.min(box.top, item.position.y),
            right: Math.max(box.right, itemRight),
            bottom: Math.max(box.bottom, itemBottom),
          };
        },
        {
          left: Infinity,
          top: Infinity,
          right: -Infinity,
          bottom: -Infinity,
        }
      );

      // Calculate offset from the bounding box top-left corner
      // If pasting onto a different slide and no explicit customPosition was provided,
      // keep absolute coordinates (offset 0) so items land at the same position.
      const baseOffset = {
        x: customPosition
          ? customPosition.x - boundingBox.left
          : isDifferentSlide
            ? boundingBox.left
            : 10,
        y: customPosition
          ? customPosition.y - boundingBox.top
          : isDifferentSlide
            ? boundingBox.top
            : 10,
      };

      const pastedItems = clipboard.map((item) => {
        // Maintain relative positions by offsetting from the bounding box
        const relativeX = item.position.x - boundingBox.left;
        const relativeY = item.position.y - boundingBox.top;

        return {
          ...item,
          id: generateUniqueId(item.type),
          position: {
            x: relativeX + baseOffset.x,
            y: relativeY + baseOffset.y,
          },
        };
      });

      // Route to header/footer if in edit mode
      if (editingArea === "header") {
        setHeaderItems((prev) => {
          const maxZ = prev.reduce((m, it) => Math.max(m, it.zIndex || 0), 0);
          return [
            ...prev,
            ...pastedItems.map((item, idx) => ({
              ...item,
              zIndex: maxZ + 1 + idx,
            })),
          ];
        });
        setSelectedItems(pastedItems.map((item) => item.id));
        return;
      }
      if (editingArea === "footer") {
        setFooterItems((prev) => {
          const maxZ = prev.reduce((m, it) => Math.max(m, it.zIndex || 0), 0);
          return [
            ...prev,
            ...pastedItems.map((item, idx) => ({
              ...item,
              zIndex: maxZ + 1 + idx,
            })),
          ];
        });
        setSelectedItems(pastedItems.map((item) => item.id));
        return;
      }

      const pageToUpdate = currentPage;
      if (!pageToUpdate) return;

      // Adjust positions to fit within page bounds (allow shapes and images outside bounds)
      const adjustedItems = pastedItems.map((item) => {
        const adjustedItem = { ...item };

        // Check if item should be constrained to page bounds
        if (item.constrainToPageBounds !== false) {
          // Apply bounds constraints (default behavior)
          if (
            adjustedItem.position.x + adjustedItem.size.width >
            pageToUpdate.size.width
          ) {
            adjustedItem.position.x =
              pageToUpdate.size.width - adjustedItem.size.width;
          }
          if (
            adjustedItem.position.y + adjustedItem.size.height >
            pageToUpdate.size.height
          ) {
            adjustedItem.position.y =
              pageToUpdate.size.height - adjustedItem.size.height;
          }
          adjustedItem.position.x = Math.max(0, adjustedItem.position.x);
          adjustedItem.position.y = Math.max(0, adjustedItem.position.y);
        }
        // If constrainToPageBounds is false, no bounds adjustment needed
        return adjustedItem;
      });

      // Update only the current page
      setPages((prevPages) =>
        prevPages.map((page) => {
          if (page.id === pageToUpdate.id) {
            const maxZ = page.items.reduce(
              (m, it) => Math.max(m, it.zIndex || 0),
              0
            );
            return {
              ...page,
              items: [
                ...page.items,
                ...adjustedItems.map((item, idx) => ({
                  ...item,
                  zIndex: maxZ + 1 + idx,
                })),
              ],
            };
          }
          return page;
        })
      );
      setSelectedItems(adjustedItems.map((item) => item.id));
    },
    [
      clipboard,
      currentPage,
      editingArea,
      setHeaderItems,
      setFooterItems,
      clipboardSourcePageId,
      pushToHistory,
    ]
  );

  const loadReport = useCallback(
    async (reportId: string): Promise<void> => {
      setIsLoadingReport(true);
      try {
        const reportToLoad = await getReport(reportId);
        if (reportToLoad) {
          // API may serialize id as `_id` or `id` depending on response config
          const resolvedId =
            reportToLoad._id ||
            (reportToLoad as { id?: string }).id ||
            reportId;
          reportToLoad._id = resolvedId;

          // Ensure pages have default background properties if they're missing
          // IMPORTANT: Preserve all page properties including items
          // Mark pages without thumbnails as dirty so they get captured on navigation/save
          const pagesWithDefaults = reportToLoad.data.pages.map((page) => ({
            ...page,
            backgroundColor: page.backgroundColor || "#ffffff",
            backgroundImage: page.backgroundImage || undefined,
            // Ensure items array is preserved
            items: (page.items || []) as ReportItem[],
            // Preserve comments (Review / presentation)
            comments: page.comments || [],
            // Mark pages without thumbnails as needing capture
            thumbnailDirty: !page.thumbnail,
          })) as Page[];

          setPages(pagesWithDefaults);
          setPagesOrder(pagesWithDefaults.map((p) => p.id));
          setHeaderItems(reportToLoad.data.headerItems || []);
          setFooterItems(reportToLoad.data.footerItems || []);
          setOrientation(reportToLoad.data.orientation || "portrait");
          setResourceSlots(reportToLoad.data.resource_slots || []);
          setConfigOptions(reportToLoad.data.config_options || {});

          setCurrentPageIndex(0);
          setSelectedItems([]);
          setEditingArea(null);
          setCurrentReportId(resolvedId);
          setCurrentReport(reportToLoad); // Set current report to trigger title update

          // Ensure the loaded report is in savedReports list
          // This handles cases where the report is loaded directly by ID before savedReports is fetched
          setSavedReports((prev) => {
            const exists = prev.some((r) => r._id === resolvedId);
            if (!exists) {
              return [...prev, reportToLoad];
            }
            return prev.map((r) => (r._id === resolvedId ? reportToLoad : r));
          });

          setHasUnsavedChanges(false); // Reset unsaved changes when loading a report

          // Clear history when loading a new report
          historyManager.clear();
          const loadedDocState: DocumentState = {
            pages: deepClone(pagesWithDefaults),
            pagesOrder: pagesWithDefaults.map((p) => p.id),
            headerItems: deepClone(reportToLoad.data.headerItems || []),
            footerItems: deepClone(reportToLoad.data.footerItems || []),
            orientation: reportToLoad.data.orientation || "portrait",
          };
          historyManager.replace(loadedDocState);

          // Auto-fit to window after the report has loaded
          setTimeout(() => {
            const currentPageData = pagesWithDefaults[0]; // Get the first page
            if (!currentPageData) return;

            // Use the zoom hook's fitToSize method
            baseZoomActions.fitToSize(
              currentPageData.size.width,
              currentPageData.size.height
            );
          }, 400); // Increased delay to ensure layout is fully settled
        }
      } catch (error) {
        console.error(`Failed to load report ${reportId}`, error);
        throw error;
      } finally {
        setIsLoadingReport(false);
      }
    },
    [baseZoomActions, deepClone, historyManager]
  );

  useEffect(() => {
    if (!currentReportId) return;
    const report = savedReports.find((r) => r._id === currentReportId);
    // Do not clear currentReport when the id is missing from savedReports —
    // review drafts are excluded from the /reports list but still loadable/saveable.
    if (report) {
      setCurrentReport(report);
    }
  }, [currentReportId, savedReports]);

  const deleteSavedReport = useCallback(
    async (reportId: string) => {
      try {
        await deleteReport(reportId);
        setSavedReports((prevReports) =>
          prevReports.filter((r) => r._id !== reportId)
        );
        if (currentReportId === reportId) {
          handleNewReport();
        }
      } catch (error) {
        console.error(`Failed to delete report ${reportId}`, error);
      }
    },
    [currentReportId]
  );

  const getReportData = useCallback((): ReportData => {
    const processItems = (items: ReportItem[]) =>
      items.map((item) => {
        // Get the current editor instance at the time of saving
        const editor = editorManager.getEditor(item.id);

        // If a managed editor instance exists for this item, get its latest HTML.
        // This is crucial for capturing style changes applied directly to the editor's DOM
        // right before saving, even if the editor is not "active".
        if (editor) {
          const content = editor.getHTML();
          // Ensure we don't save empty placeholder content
          if (content && content !== "<p></p>") {
            const normalizeFont = (val: any) =>
              typeof val === "number" ? `${val}px` : val;
            const normalizedItem = {
              ...item,
              content,
              fontSize: normalizeFont((item as any).fontSize),
              tableConfig: item.tableConfig
                ? {
                    ...item.tableConfig,
                    fontSize: normalizeFont(item.tableConfig.fontSize),
                    headerFontSize: normalizeFont(
                      item.tableConfig.headerFontSize
                    ),
                    decimalPlaces:
                      typeof item.tableConfig.decimalPlaces === "number"
                        ? item.tableConfig.decimalPlaces
                        : 2,
                  }
                : item.tableConfig,
            } as any;
            return normalizedItem;
          }
        }

        // Fallback for the legacy active editor logic
        const currentActiveEditor = editorManager.getActiveEditor();
        if (currentActiveEditor && item.id === editorManager.activeEditorId) {
          const content = currentActiveEditor.getHTML();
          if (content && content !== "<p></p>") {
            return {
              ...item,
              content,
            };
          }
        }
        const normalizeFont = (val: any) =>
          typeof val === "number" ? `${val}px` : val;
        return {
          ...item,
          fontSize: normalizeFont((item as any).fontSize),
          tableConfig: item.tableConfig
            ? {
                ...item.tableConfig,
                fontSize: normalizeFont(item.tableConfig.fontSize),
                headerFontSize: normalizeFont(item.tableConfig.headerFontSize),
                decimalPlaces:
                  typeof item.tableConfig.decimalPlaces === "number"
                    ? item.tableConfig.decimalPlaces
                    : 2,
              }
            : item.tableConfig,
        } as any;
      });

    const pagesData: ApiPage[] = orderedPages.map((page) => {
      const processedPage: ApiPage = {
        id: page.id,
        name: page.name,
        items: processItems(page.items),
        size: page.size,
        isTitlePage: page.isTitlePage,
        padding: page.padding,
        backgroundColor: page.backgroundColor || "#ffffff",
        backgroundImage: page.backgroundImage || undefined,
        thumbnail: page.thumbnail,
        comments: page.comments,
      };

      return processedPage;
    });

    return {
      pages: pagesData,
      headerItems: processItems(headerItems),
      footerItems: processItems(footerItems),
      orientation,
      resource_slots: resourceSlots,
      config_options: configOptions,
    };
  }, [
    orderedPages,
    headerItems,
    footerItems,
    orientation,
    resourceSlots,
    configOptions,
    // Removed editorManager and activeEditor from dependencies to prevent infinite loops
  ]);

  const saveReportAs = useCallback(
    async (name: string, options?: { description?: string | null }) => {
      const reportData = getReportData();
      const createPayload: CreateReportRequest = {
        name,
        data: reportData,
      };
      if (options?.description !== undefined) {
        const t = options.description?.trim();
        createPayload.description = t ? t : null;
      } else if (currentReport?.description?.trim()) {
        createPayload.description = currentReport.description.trim();
      }
      const tagIds = (currentReport?.tags ?? []).filter(
        (id): id is string => typeof id === "string" && id.trim() !== "",
      );
      if (tagIds.length > 0) {
        createPayload.tags = tagIds;
      }
      try {
        const newReport = await createReport(createPayload);
        setSavedReports((prev) => {
          const currentReports = Array.isArray(prev) ? prev : [];
          return [...currentReports, newReport];
        });
        setCurrentReportId(newReport._id);
        setCurrentReport(newReport); // Immediately set current report to trigger title update
        setHasUnsavedChanges(false); // Reset unsaved changes after successful save
        return newReport;
      } catch (error) {
        console.error("Failed to save new report", error);
        throw error; // Propagate error to caller
      }
    },
    [getReportData, currentReport]
  );

  const updateReportTags = useCallback(
    (tagIds: string[]) => {
      const normalized = [
        ...new Set(
          tagIds.filter(
            (id): id is string => typeof id === "string" && id.trim() !== "",
          ),
        ),
      ];
      setCurrentReport((prev) => {
        if (prev) {
          return { ...prev, tags: normalized };
        }
        return {
          name: "Untitled Report",
          tags: normalized,
        } as ApiReport;
      });
      if (currentReportId) {
        setSavedReports((prev) =>
          prev.map((r) =>
            r._id === currentReportId ? { ...r, tags: normalized } : r,
          ),
        );
      }
      tagsDirtyRef.current = true;
      markUnsavedAndTriggerAutoSave();
    },
    [currentReportId, markUnsavedAndTriggerAutoSave],
  );

  /**
   * Updates the current report name and marks as having unsaved changes.
   * Also syncs the name to savedReports to prevent state overwrites.
   * For new unsaved reports (currentReport is null), creates a minimal report object.
   *
   * @param name - The new report name
   */
  const updateReportName = useCallback(
    (name: string) => {
      setCurrentReport((prev) => {
        if (prev) {
          return { ...prev, name };
        }
        // For new unsaved reports, create a minimal report object with the name
        return { name } as ApiReport;
      });
      if (currentReportId) {
        setSavedReports((prev) =>
          prev.map((r) => (r._id === currentReportId ? { ...r, name } : r))
        );
      }
      markUnsavedAndTriggerAutoSave();
    },
    [currentReportId, markUnsavedAndTriggerAutoSave],
  );

  const updateReportDescription = useCallback(
    (description: string) => {
      const normalized = description.trim() === "" ? null : description;
      setCurrentReport((prev) => {
        if (prev) {
          return { ...prev, description: normalized };
        }
        return {
          name: "Untitled Report",
          description: normalized,
        } as ApiReport;
      });
      if (currentReportId) {
        setSavedReports((prev) =>
          prev.map((r) =>
            r._id === currentReportId ? { ...r, description: normalized } : r,
          ),
        );
      }
      markUnsavedAndTriggerAutoSave();
    },
    [currentReportId, markUnsavedAndTriggerAutoSave],
  );

  const saveReport = useCallback(
    async (
      overrideData?: Partial<ReportData>,
      options?: { generatorReview?: boolean },
    ) => {
      // Gate capture by dirty flag and schedule during idle with a hard timeout
      let latestThumbnail: string | null = null;
      const shouldCapture =
        !!currentPage && (currentPage as any).thumbnailDirty === true;
      if (shouldCapture) {
        try {
          const gotIdle = await waitForIdle(100);
          if (gotIdle) {
            // Bound capture duration to avoid blocking saves
            const timeout = new Promise<null>((resolve) =>
              setTimeout(() => resolve(null), 150)
            );
            latestThumbnail = await Promise.race([
              captureCanvasThumbnail(),
              timeout,
            ]);
            if (latestThumbnail && currentPage) {
              // Update local state for immediate UI feedback and clear dirty flag
              // Use updatePageThumbnail to avoid setting thumbnailDirty: true again
              updatePageThumbnail(currentPage.id, latestThumbnail);
            }
          }
        } catch {
          // Ignore capture errors; saving should still proceed
        }
      }

      // Build report data after attempting capture, ensuring inclusion if available
      const reportData = { ...getReportData(), ...overrideData };
      if (latestThumbnail && currentPage) {
        reportData.pages = reportData.pages.map((p) =>
          p.id === currentPage.id ? { ...p, thumbnail: latestThumbnail! } : p
        );
      }

      if (currentReportId) {
        try {
          // Prefer in-memory currentReport — review drafts are not in /reports list
          // (type=generator_review_draft) so savedReports.find often fails.
          const reportMeta =
            currentReport &&
            (currentReport._id === currentReportId ||
              (currentReport as { id?: string }).id === currentReportId)
              ? currentReport
              : savedReports.find((r) => r._id === currentReportId);

          if (!reportMeta) {
            console.error(
              "Cannot save: no metadata for current report. Attempting save with defaults.",
              { currentReportId },
            );
          }

          const tagIds = (reportMeta?.tags ?? []).filter(
            (id): id is string => typeof id === "string" && id.trim() !== "",
          );

          // Review URL / explicit flag: never allow writing generator master templates.
          // Reports admin (and normal builder) may edit those templates with allowTemplateEdit.
          const reviewFromUrl =
            typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).get("review") ===
              "true";
          const generatorReview = Boolean(
            options?.generatorReview || reviewFromUrl,
          );

          const updatePayload: UpdateReportRequest = {
            name: reportMeta?.name ?? "Report",
            description: reportMeta?.description ?? null,
            data: reportData,
          };
          // Only re-send tags when the user changed them in this session.
          // Re-sending stale template label ids causes 400 on save/export.
          if (!generatorReview && tagsDirtyRef.current) {
            updatePayload.tags = tagIds;
          }

          const updatedReport = await updateReport(
            currentReportId,
            updatePayload,
          );
          setSavedReports((prev) => {
            const exists = prev.some((r) => r._id === currentReportId);
            if (exists) {
              return prev.map((r) =>
                r._id === currentReportId ? updatedReport : r,
              );
            }
            return [...prev, updatedReport];
          });
          setCurrentReport(updatedReport);
          setHasUnsavedChanges(false);
          tagsDirtyRef.current = false;
          return updatedReport;
        } catch (error) {
          console.error("Failed to update report", error);
          throw error; // Propagate error to auto-save logic
        }
      } else {
        console.error(
          "saveReport called without a currentReportId. The UI should handle this and call saveReportAs."
        );
        throw new Error("Cannot save: no report id loaded");
      }
    },
    [
      currentReportId,
      currentReport,
      getReportData,
      savedReports,
      currentPage,
      updatePageThumbnail,
    ]
  );

  // Manual history saving - only called from user action functions

  // Global keyboard shortcuts for undo/redo and layer management
  useEffect(() => {
    const shouldBypassGlobalUndoRedo = () => {
      if (editingItemId) {
        return true;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) {
        return false;
      }

      const activeTag = activeElement.tagName.toLowerCase();
      if (
        activeTag === "input" ||
        activeTag === "textarea" ||
        activeTag === "select"
      ) {
        return true;
      }

      return (
        activeElement.isContentEditable ||
        !!activeElement.closest('[contenteditable="true"]') ||
        !!activeElement.closest(".ProseMirror")
      );
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Use appropriate modifier key for platform (Cmd on Mac, Ctrl on PC)
      const isModifierPressed = e.metaKey || e.ctrlKey;

      // Handle undo/redo
      if (isModifierPressed && e.key.toLowerCase() === "z") {
        if (shouldBypassGlobalUndoRedo()) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      // Also support Ctrl+Y for redo on Windows
      if (e.ctrlKey && e.key.toLowerCase() === "y" && !e.metaKey) {
        if (shouldBypassGlobalUndoRedo()) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        redo();
      }

      // Handle layer movement shortcuts
      if (isModifierPressed && selectedReportItem) {
        const items = getAreaItems();
        const currentZ = selectedReportItem.zIndex || 0;
        const sorted = [...items].sort(
          (a, b) => (a.zIndex || 0) - (b.zIndex || 0)
        );
        const index = sorted.findIndex((i) => i.id === selectedReportItem.id);
        let newZ = currentZ;

        // Using PageUp/PageDown for layer movement (more standard in design software)
        if (e.key === "PageUp") {
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) {
            // Bring to Front (Cmd/Ctrl + Shift + PageUp)
            const maxZ = Math.max(0, ...items.map((i) => i.zIndex || 0));
            newZ = maxZ + 1;
          } else {
            // Bring Forward (Cmd/Ctrl + PageUp)
            const next = sorted[index + 1];
            if (next) newZ = (next.zIndex || 0) + 1;
            else newZ = (sorted[index]?.zIndex || 0) + 1;
          }
          pushToHistory();
          updateReportItem(selectedReportItem.id, { zIndex: newZ });
        } else if (e.key === "PageDown") {
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) {
            // Send to Back (Cmd/Ctrl + Shift + PageDown)
            const minZ = Math.min(0, ...items.map((i) => i.zIndex || 0));
            newZ = minZ - 1;
          } else {
            // Send Backward (Cmd/Ctrl + PageDown)
            const prev = sorted[index - 1];
            if (prev) newZ = (prev.zIndex || 0) - 1;
            else newZ = (sorted[index]?.zIndex || 0) - 1;
          }
          pushToHistory();
          updateReportItem(selectedReportItem.id, { zIndex: newZ });
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true); // Use capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [undo, redo, selectedReportItem, updateReportItem, pushToHistory, editingItemId]);

  const handleNewReport = useCallback(() => {
    // Create a fresh page with a new unique ID to avoid stale references
    const freshPage: Page = {
      id: generateUniqueId("page"),
      name: "Page 1",
      items: [],
      size: initialPage.size,
      isTitlePage: false,
      padding: 0,
      backgroundColor: "#ffffff",
      backgroundImage: undefined,
    };
    setPages([freshPage]);
    setPagesOrder([freshPage.id]);
    setCurrentPageIndex(0);
    setSelectedItems([]);
    setEditingItemId(null);
    setEditingArea(null);
    setHeaderItems([]);
    setFooterItems([]);
    setClipboard([]);
    setCurrentReportId(null);
    setCurrentReport(null); // Reset currentReport for new unsaved report
    setOrientation("portrait");
    setResourceSlots([]);
    setConfigOptions({});
    historyManager.clear(); // Clear history using new history manager
    const freshDocState: DocumentState = {
      pages: deepClone([freshPage]),
      pagesOrder: [freshPage.id],
      headerItems: [],
      footerItems: [],
      orientation: "portrait",
    };
    historyManager.replace(freshDocState);
    setHasUnsavedChanges(false); // Reset unsaved changes when creating new report

    // Reset zoom to default
    baseZoomActions.resetZoom();

    editorManager.disableAllEditing();

    // Auto-fit to window after the canvas has rendered
    setTimeout(() => {
      // Use the zoom hook's fitToSize method with initial page dimensions
      baseZoomActions.fitToSize(freshPage.size.width, freshPage.size.height);
    }, 400); // Increased delay to ensure layout is fully settled
  }, [baseZoomActions, deepClone, historyManager]);

  // Deep-link / browser back: sync current page from ?page= only. When the param
  // is absent, default page 0 is already useState(0); do not write index from
  // here — a brief window existed where state updated before setSearchParams
  // committed, and treating "no param" as "force page 0" bounced the selection.
  useEffect(() => {
    if (!searchParams) return;
    const pageParam = searchParams.get("page");
    if (!pageParam) return;

    const pageIndex = parseInt(pageParam, 10);
    if (
      !isNaN(pageIndex) &&
      pageIndex >= 0 &&
      pageIndex < pages.length &&
      pageIndex !== currentPageIndex
    ) {
      setCurrentPageIndex(pageIndex);
      setSelectedItems([]);
      setEditingItemId(null);
      setEditingArea(null);
      editorManager.disableAllEditing();
    }
  }, [searchParams, pages.length, currentPageIndex]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const value = useMemo(() => ({
    pages: orderedPages,
    setPages,
    pagesOrder,
    setPagesOrder,
    currentPageIndex,
    setCurrentPageIndex,
    selectedItems,
    setSelectedItems,
    editingItemId,
    setEditingItemId,
    editingArea,
    setEditingArea,
    headerItems,
    setHeaderItems,
    footerItems,
    setFooterItems,
    clipboard,
    setClipboard,
    clipboardSourcePageId,
    setClipboardSourcePageId,
    savedReports,
    setSavedReports,
    currentReportId,
    setCurrentReportId,
    currentReport,
    setCurrentReport,
    orientation,
    setOrientation,
    resourceSlots,
    setResourceSlots,
    configOptions,
    setConfigOptions,
    companyTheme,
    isLoadingTheme,
    editorManager,
    activeEditor,
    currentPage,
    selectedReportItems,
    selectedReportItem,
    addReportItem,
    updateReportItem,
    deleteItem,
    addPage,
    duplicatePage,
    deletePage,
    reorderPages,
    toggleTitlePage,
    renamePage,
    updatePage,
    handleOrientationChange,
    handleSelect,
    handleCopy,
    handleCut,
    handlePaste,
    loadReport,
    deleteSavedReport,
    saveReport,
    saveReportAs,
    updateReportName,
    updateReportDescription,
    updateReportTags,
    handleNewReport,
    createEditorExtensions,
    findReportItem,
    availableColumns,
    availableExposures,
    availableStatistics,
    updatePagePadding,
    navigateToPage,
    undo,
    redo,
    canUndo,
    canRedo,
    startHistoryBatch,
    endHistoryBatch,
    pushToHistory,
    syncHistoryWithDocument,
    zoom,
    zoomActions,
    reportBuilderView,
    setReportBuilderView,
    autoSaveEnabled,
    setAutoSaveEnabled,
    markUnsavedAndTriggerAutoSave,
    setOpenChartDataAssignmentHandler,
    requestOpenChartDataAssignment,
  }), [
    orderedPages,
    pagesOrder,
    currentPageIndex,
    selectedItems,
    editingItemId,
    editingArea,
    headerItems,
    footerItems,
    clipboard,
    clipboardSourcePageId,
    savedReports,
    currentReportId,
    currentReport,
    orientation,
    resourceSlots,
    configOptions,
    companyTheme,
    isLoadingTheme,
    editorManager,
    activeEditor,
    currentPage,
    selectedReportItems,
    selectedReportItem,
    addReportItem,
    updateReportItem,
    deleteItem,
    addPage,
    duplicatePage,
    deletePage,
    reorderPages,
    toggleTitlePage,
    renamePage,
    updatePage,
    handleOrientationChange,
    handleSelect,
    handleCopy,
    handleCut,
    handlePaste,
    loadReport,
    deleteSavedReport,
    saveReport,
    saveReportAs,
    updateReportName,
    updateReportDescription,
    updateReportTags,
    handleNewReport,
    createEditorExtensions,
    findReportItem,
    availableColumns,
    availableExposures,
    availableStatistics,
    updatePagePadding,
    navigateToPage,
    undo,
    redo,
    canUndo,
    canRedo,
    startHistoryBatch,
    endHistoryBatch,
    pushToHistory,
    syncHistoryWithDocument,
    zoom,
    zoomActions,
    reportBuilderView,
    setReportBuilderView,
    autoSaveEnabled,
    setAutoSaveEnabled,
    markUnsavedAndTriggerAutoSave,
    setOpenChartDataAssignmentHandler,
    requestOpenChartDataAssignment,
  ]);

  const contextValue = useMemo(() => ({
    ...value,
    reportsLoading,
    isSaving,
    lastSavedTime,
    showSaveSuccess,
    saveError,
    showSaveError,
    hasUnsavedChanges,
    isLoadingReport,
  }), [
    value,
    reportsLoading,
    isSaving,
    lastSavedTime,
    showSaveSuccess,
    saveError,
    showSaveError,
    hasUnsavedChanges,
    isLoadingReport,
  ]);

  // Update global context for DynamicMention extension
  useEffect(() => {
    setReportBuilderContextForMentions(contextValue);
  }, [contextValue]);

  return (
    <ReportBuilderContext.Provider value={contextValue}>
      {children}
    </ReportBuilderContext.Provider>
  );
};

// Custom Hook
export const useReportBuilder = () => {
  const context = useContext(ReportBuilderContext);
  if (!context) {
    throw new Error(
      "useReportBuilder must be used within a ReportBuilderProvider"
    );
  }
  return context;
};

/**
 * Safe version of useReportBuilder that returns null when context is not available.
 * Use this in components/hooks that may be rendered outside of ReportBuilderProvider.
 */
export const useReportBuilderSafe = () => {
  const context = useContext(ReportBuilderContext);
  return context;
};
